import { Injectable } from "@angular/core";
import { AbstractControl, ValidatorFn } from "@angular/forms";
import { LoadingService } from "./loading.service";
import { Router } from "@angular/router";
import { AlertService } from "./alert.service";
import { ToastrService } from "ngx-toastr";
import { ProjectConfigService } from "./project-config.service";
import { Platform } from "@angular/cdk/platform";

@Injectable({
  providedIn: 'root'
})
export class XtrasService {

  constructor(
    private alert: AlertService,
    private load: LoadingService,
    private toast: ToastrService,
    private router: Router,
    private projectConfig: ProjectConfigService,
    public platform: Platform,
  ) {}

  filterEmptyParams(formData: { [key: string]: any }) {
    return Object.fromEntries(
      Object.entries(formData).filter(
        ([_, value]) => value !== null && value !== undefined && value !== ''
      )
    );
  }

  lengthItems(items: any) {
    if(Array.isArray(items)) {
      if(items.length >= 1) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  scrollToTarget(el: HTMLElement) { // example <div #algo> scrollToTarget(algo)
    el.scrollIntoView({behavior: 'smooth'});
  }

  checkFieldsSame(field1: string, field2: string): ValidatorFn {
    return (group: AbstractControl): { [key: string]: any } | null => {
      const _field1 = group.get(field1);
      const _field2 = group.get(field2);
      return _field1 && _field2 && _field1.value === _field2.value ? null : { notSame: true };
    };
  }

  getErrorApi(result: any, loading:boolean = false, redirectError: string = '/', errorDefault?:string) {
    if(loading) {
      this.load.dismiss();
    }

    if(result?.others?.redirect_url) {
      this.router.navigateByUrl(result?.others?.redirect_url);
      this.alertErrorApi(result, errorDefault);
      return;
    }
    if(result['redirect']) {
      this.router.navigateByUrl(redirectError);
    }
    this.alertErrorApi(result, errorDefault);
  }

  alertErrorApi(result: any, errorDefault?: string) {
    if(result['message']) {
      if(result['errors']) {
        this.alert.alertCapYei({
          type: 'error',
          message: result['message'],
          buttons: {
            cancel: { show: false },
            confirm: { show: true, text: 'Entendido' },
          },
          description: result['errors']
        }).then(() => {
          this.reloadPage(result?.others?.reload ?? false);
        });
      } else {
        this.alert.alertCapYei({
          type: 'error',
          message: 'Error',
          buttons: {
            cancel: { show: false },
            confirm: { show: true, text: 'Entendido' },
          },
          description: result['message']
        }).then(() => {
          this.reloadPage(result?.others?.reload ?? false);
        });
      }
    } else {
      if(result['errors']) {
        this.alert.alertCapYei({
          type: 'error',
          message: '¡Error!',
          buttons: {
            cancel: { show: false },
            confirm: { show: true, text: 'Entendido' },
          },
          description: result['errors']
        }).then(() => {
          this.reloadPage(result?.others?.reload ?? false);
        });
      } else {
        this.toast.error((errorDefault ?? 'No se pudo obtener la información, intente nuevamente.'), '¡Error!',  {timeOut: 5000, closeButton: true, progressBar: true});
        if(result?.others?.reload) {
          setTimeout(() => {
            this.reloadPage(true);
          }, 3000);
        }
      }
    }
  }

  async reloadPage(reload: boolean = false) {
    if(reload) {
      const currentUrl = this.router.url;
      await this.router.navigate(['/'], { skipLocationChange: true, replaceUrl: true });
      await this.router.navigate([currentUrl], { skipLocationChange: true, replaceUrl: true });
    }
  }

  loadInlineScriptHead(text: string, noscript: boolean = false) {
    const script:any = document.createElement('script');
    if(!noscript) {
      script.type = 'text/javascript';
      script.text = text;
      document.head.appendChild(script);
    } else {
      const noscript = document.createElement('noscript');
      noscript.innerHTML = text;
      document.head.appendChild(noscript);
    }
  }

  loadInlineScriptBody(text: string, noscript: boolean = false) {
    const script:any = document.createElement('script');
    if(!noscript) {
      script.type = 'text/javascript';
      script.text = text;
      document.body.appendChild(script);
    } else {
      const noscript = document.createElement('noscript');
      noscript.innerHTML = text;
      document.body.appendChild(noscript);
    }
  }

  loadScriptBody(src: string, async: boolean = false, param?: { param: string, value: string }) {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    script.async = async;
    if(param) {
      (script as any)[param.param] = param.value;
    }
    document.body.appendChild(script);
  }

  loadScriptHead(src: string, async: boolean = false) {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = src;
    script.async = async;
    document.head.appendChild(script);
  }

  staticMap(lat: string|number, lng: string|number) {
    return `https://maps.googleapis.com/maps/api/staticmap?key=${this.projectConfig.getConfig().keyMap}&size=500x500&zoom=17&maptype=roadmap&markers=color:red|${lat},${lng}`;
  }

  // Determina si un color es claro u oscuro
  determineColorType(color: string): 'light' | 'dark' | 'normal' | 'unknown' {
    try {
      // Convertimos el color a formato RGBA
      const rgba = this.parseColorToRGBA(color);
      if (!rgba) return 'unknown';

      // Calculamos el brillo usando la fórmula de luminosidad perceptiva
      const [r, g, b] = rgba;
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;

      // Definimos umbrales más estrictos
      const LIGHT_THRESHOLD = 200;  // Más cerca del blanco (255)
      const DARK_THRESHOLD = 60;    // Más cerca del negro (0)

      if (brightness >= LIGHT_THRESHOLD) {
        return 'light';
      } else if (brightness <= DARK_THRESHOLD) {
        return 'dark';
      } else {
        return 'normal';  // Colores en el rango medio
      }
    } catch {
      return 'unknown';
    }
  }

  // Convierte cualquier formato de color a RGBA
  parseColorToRGBA(color: string): [number, number, number, number] | null {
    // Eliminar espacios y convertir a minúsculas
    const normalized = color.trim().toLowerCase();

    // 1. Hex color (#rgb, #rgba, #rrggbb, #rrggbbaa)
    if (/^#([a-f0-9]{3,4}|[a-f0-9]{6}|[a-f0-9]{8})$/.test(normalized)) {
      let hex = normalized.substring(1);

      // Expandir formato corto (#rgb → #rrggbb)
      if (hex.length === 3 || hex.length === 4) {
        hex = hex.split('').map(c => c + c).join('');
      }

      // Parsear componentes
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const a = hex.length === 8 ? parseInt(hex.substring(6, 8), 16) / 255 : 1;

      return [r, g, b, a];
    }

    // 2. RGB/RGBA color (rgb(255, 255, 255) o rgba(255, 255, 255, 0.5))
    const rgbMatch = normalized.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)$/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]);
      const g = parseInt(rgbMatch[2]);
      const b = parseInt(rgbMatch[3]);
      const a = rgbMatch[4] ? parseFloat(rgbMatch[4]) : 1;
      return [r, g, b, a];
    }

    // 3. Nombres de colores básicos (opcional)
    const colorNames: Record<string, [number, number, number, number]> = {
      'white': [255, 255, 255, 1],
      'black': [0, 0, 0, 1],
      'red': [255, 0, 0, 1],
      'green': [0, 128, 0, 1],
      'blue': [0, 0, 255, 1],
      // Añadir más colores según necesidad
    };

    if (colorNames[normalized]) {
      return colorNames[normalized];
    }

    return null;
  }

  isAppIos() {
    return this.platform.IOS;
  }

  isAppAndroid() {
    return this.platform.ANDROID;
  }

  isBrowser() {
    return this.platform.isBrowser;
  }

  isAppleDevice() {
    let _window: any|Window = window;
    const userAgent = navigator.userAgent || navigator.vendor || _window.opera;
    const platform = (navigator.platform || '').toLowerCase();

    // 1. Detección de iOS/iPadOS/iPhone/iPod
    // Buscamos las cadenas específicas de dispositivos móviles Apple
    if (/iPad|iPhone|iPod/.test(userAgent) && !_window.MSStream) {
      return true; // Es un dispositivo móvil Apple (iOS/iPadOS)
    }

    // 2. Detección de macOS (PC) y iPadOS moderno
    // Los SO de Apple (macOS y iPadOS 13+) tienen 'Mac' o 'mac' en la propiedad platform
    if (platform.includes('mac')) {
      // Esto cubre tanto macOS (PC) como iPadOS (móvil, que se disfraza de Mac)
      return true; // Es un Mac (macOS) o un iPad moderno (iPadOS)
    }

    // Si no se cumple ninguna de las condiciones anteriores, no es un dispositivo Apple.
    return false;
  }

  isMobileDevice(): boolean {
    // Detectar dispositivos móviles comunes
    const userAgent = navigator.userAgent;
    return (
      this.platform.IOS ||
      this.platform.ANDROID ||
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile/i.test(userAgent)
    );
  }

  async downloadFile(url: string, fileName: string, textDownload?: string) {
    try {
      this.load.loadingShow(undefined, textDownload ?? 'Descargando...');
      // Hacer una petición fetch para obtener el archivo
      const response = await fetch(url);
      const blob = await response.blob();
      
      // Crear objeto URL desde el blob
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Crear enlace temporal para descarga
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      
      // Limpiar
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      this.load.dismiss();
    } catch (error) {
      console.error('Error al descargar el archivo:', error);
      // Fallback: abrir en nueva pestaña
      this.load.dismiss();
      window.open(url, '_blank');
    }
  }
}
